import "./share.scss";
import { ComponentRegistry } from '@ts/utils/ComponentRegistry';
import { dom, icon } from "@fortawesome/fontawesome-svg-core";
import { faCircleCheck } from "@fortawesome/free-solid-svg-icons";

type ShareHook = () => void | Promise<void>;
type ShareSuccessHook = (trigger: HTMLButtonElement | HTMLAnchorElement) => void | Promise<void>;
type ShareFailHook = (error: unknown) => void | Promise<void>;
type ShareOptions = {
  onClick?: ShareHook;
  onSuccess?: ShareSuccessHook;
  onFail?: ShareFailHook;
  onCancel?: ShareHook;
  onUnsupported?: ShareHook;
  url?: string;
  text?: string;
  title?: string;
};
type webShareApiOptions = {
    title?: string;
    text?: string;
    url?: string;
}

export class Share {
    private readonly id: string;
    private static registry = new ComponentRegistry<Share>('share');
    private readonly trigger: HTMLButtonElement | HTMLAnchorElement;
    private readonly _confirmShareUrl: string | undefined;
    
    private _config = {
        supportsWebShareApi: false,
        isMobile: false,
        successDelay: 1000,
        shareBtnClass: 'share-btn'
    };

    private _shareTypes = {
        webShareApi: 0,
        clipboard: 1,
        sms: 2
    }

    private constructor(
        trigger: HTMLButtonElement | HTMLAnchorElement,
        private options: ShareOptions
    ) {
        this.id = Share.registry.register(trigger, this);
        this.trigger = trigger;
        this._confirmShareUrl = trigger.getAttribute('data-confirm-share');
        
        this.webShareApi = Boolean(typeof navigator.share !== "undefined");
        this.isMobileDevice = Boolean(this.deviceType() !== "desktop");
        
        this.trigger.classList.add(this._config.shareBtnClass);
        this.setClickEvent();
        
        if (!this.webShareApi) {
            this.options.onUnsupported?.();
            this.setCopyButtonLabel();
        }
    }

    private setClickEvent() {
        this.trigger.addEventListener('click', async (e) => {
            e.preventDefault();
            
            if (typeof this.options.onClick === 'function') {
                await this.options.onClick();
            }

            try {
                await this.performShare();
                
                if (typeof this.options.onSuccess === 'function') {
                    await this.options.onSuccess(this.trigger);
                }
                await this.confirmShare();
                this.setSuccessBtn();
            } catch (err: unknown) {
                const name = (err as DOMException)?.name ?? (err as any)?.name;
                if (name === 'AbortError') {
                    await this.options.onCancel?.();
                    console.log('Share cancelled:', err);
                } else {
                    if (typeof this.options.onFail === 'function') {
                        await this.options.onFail(err);
                    }
                    console.log('Share failed:', err);
                }
            }
        });
    }

    private setSuccessBtn() {
        this.trigger.classList.add('share-success');

        const svgIcon = this.trigger.querySelector('svg');
        if (!svgIcon) return;
        
        const originalSvgClone = svgIcon.cloneNode(true) as SVGElement;

        const checkSvg = this.createIconSvg(faCircleCheck);
        svgIcon.replaceWith(checkSvg);

        window.setTimeout(() => {
            this.trigger.classList.remove('share-success');
            const currentSvg = this.trigger.querySelector('svg');
            if (currentSvg) {
                currentSvg.replaceWith(originalSvgClone);
            }  
        }, this._config.successDelay);
    }

    private createIconSvg(iconDefinition: any): SVGElement {
        const iconMarkup = icon(iconDefinition);
        const svgHtml = iconMarkup.html[0] ?? '';
        const fragment = document.createRange().createContextualFragment(svgHtml);
        const svg = fragment.firstElementChild as SVGElement;
        svg.setAttribute('aria-hidden', 'true');
        return svg;
    }

    private async setCopyButtonLabel() {
        const buttonLabel = this.trigger.querySelector('.btn-label');
        const label = this.trigger.dataset?.copyLabel;
        await dom.i2svg({ node: this.trigger });
    
        if (label && buttonLabel) {
            const svgIcon = this.trigger.querySelector('svg');
            buttonLabel.textContent = label;
            if (svgIcon) svgIcon.setAttribute('data-icon', 'copy');
        }
    }

    private async performShare() {
        const data: webShareApiOptions = {};
        if (this.options.title) data.title = this.options.title;
        if (this.options.text) data.text = this.cleanShareText(this.options.text);
        if (this.options.url) data.url = this.options.url;

        if (this.webShareApi) {
            await navigator.share(data);
            return;
        }
        await this.copyToClipboard(data.url || data.text || '');
    }

    private async copyToClipboard(text: string) {
        if (navigator.clipboard?.writeText) {
            await navigator.clipboard.writeText(text);
        } else {
            const ta = document.createElement('textarea');
            ta.value = text;
            document.body.appendChild(ta);
            ta.select();
            document.execCommand('copy');
            document.body.removeChild(ta);
        }
    }

    private async confirmShare(): Promise<void> {
        if (!this._confirmShareUrl) return;

        const response = await fetch(this._confirmShareUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: '{}'
        });

        if (!response.ok) {
            throw new Error(`Confirm share failed: ${response.status}`);
        }
    }

    get shareType() {
        return this._shareTypes;
    }

    get isMobileDevice(): boolean { return this._config.isMobile; }
    set isMobileDevice(status: boolean) { this._config.isMobile = status; }

    get webShareApi(): boolean { return this._config.supportsWebShareApi; }
    set webShareApi(status: boolean) { this._config.supportsWebShareApi = status; }

    private deviceType () {
        const ua = navigator.userAgent;
        if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
            return "tablet";
        }
        else if (/Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua)) {
            return "mobile";
        }
        return "desktop";
    }

    private cleanShareText (text: string) {
        if (!text) return '';
        const cleanedText = text.replace(/\u00A0/g, ' ')
            .replace(/\r\n/g, '\n')
            .replace(/[ \t]+/g, ' ')
            .replace(/\n{2,}/g, '\n')
            .trim();
        return cleanedText;
    }

    public static create(
        target: string | HTMLElement | NodeListOf<HTMLElement> | HTMLElement[],
        options: ShareOptions = {}
    ): Share[] | null {
        const triggers = ComponentRegistry.processTargets<Share, HTMLElement>(
            Share.registry,
            target,
            [HTMLButtonElement, HTMLAnchorElement]
        ) as (HTMLButtonElement | HTMLAnchorElement)[];

        if (!triggers.length) return null;
        
        return triggers.map(trigger => new Share(trigger, options));
    }

    public static getShare(id: string): Share | null {
        return Share.registry.get(id) ?? null;
    }

    public destroy(): void {
        Share.registry.unregister(this.id);
    }
}